import React from "react";
import { useTheme } from "hooks";
import "./LegalPage.styles.scss";
import quickbooksLogo from "assets/img/quickbooks-brand-preferred-logo.png";
import dgiiLogo from "assets/img/dgii.png";

export default function QuickBooksIntegrationPage() {
  const { theme } = useTheme();
  const accentColor = (theme && theme.color1) || "#0077b6";

  return (
    <div className="legal-page" style={{ "--legal-accent": accentColor }}>
      {/* ── Header ── */}
      <header className="legal-header">
        <div className="legal-header-inner">
          <div className="legal-header-brand">
            {theme && theme.logo ? (
              <img src={theme.logo} alt="Citrus eCF" />
            ) : (
              <span>Citrus eCF</span>
            )}
          </div>
          <span className="legal-header-tag">Integración QuickBooks</span>
        </div>
      </header>

      {/* ── Content ── */}
      <div className="legal-body">
        <div className="legal-container">

          {/* Document header */}
          <div className="legal-doc-header">
            <span className="legal-doc-badge">Learn More</span>
            <h1>Integración Citrus eCF con QuickBooks Online</h1>
            <p className="legal-subtitle">
              Cómo funciona, cómo conectarse, cómo desconectarse y qué datos
              fluyen entre ambos sistemas.
            </p>
            <div className="legal-meta">
              <span><strong>Última Actualización:</strong> 7 de mayo de 2026</span>
              <span><strong>Audiencia:</strong> Empresas en República Dominicana</span>
            </div>
          </div>

          {/* Section 1 */}
          <div className="legal-section">
            <h2>1. ¿Qué es Citrus eCF?</h2>
            <p>
              <strong>Citrus eCF</strong> es una plataforma empresarial de
              facturación electrónica diseñada exclusivamente para la{" "}
              <strong>República Dominicana</strong>. Está construida para
              cumplir con las regulaciones de la{" "}
              <strong>Dirección General de Impuestos Internos (DGII)</strong>,
              autoridad fiscal del país, generando, firmando, transmitiendo y
              gestionando <strong>Comprobantes Fiscales Electrónicos (e-CF)</strong>{" "}
              en cumplimiento con la Ley 32-23 y normas complementarias.
            </p>
            <p>
              La plataforma soporta los principales tipos de comprobantes
              fiscales electrónicos del régimen dominicano: Facturas de Crédito
              Fiscal, Facturas de Consumo, Notas de Crédito, Notas de Débito,
              Comprobantes de Compras, Comprobantes Gubernamentales,
              Comprobantes para Exportaciones, Comprobantes de Régimen Especial,
              entre otros.
            </p>
          </div>

          {/* Section 2 */}
          <div className="legal-section">
            <h2>2. ¿Por qué integrarse con QuickBooks Online?</h2>
            <p>
              La integración entre <strong>Citrus eCF</strong> y{" "}
              <strong>QuickBooks Online</strong> permite a las empresas
              dominicanas que utilizan QuickBooks como su sistema contable
              cumplir con la facturación electrónica de la DGII sin necesidad
              de capturar la información dos veces.
            </p>
            <p>Beneficios principales:</p>
            <ul>
              <li>
                <strong>Cero doble captura:</strong> los documentos creados en
                QuickBooks (Invoices, Purchase Orders, Credit Memos) se procesan
                automáticamente como e-CF.
              </li>
              <li>
                <strong>NCF dentro de QuickBooks:</strong> el Número de
                Comprobante Fiscal asignado por la DGII se escribe de vuelta
                como un campo personalizado en el documento original.
              </li>
              <li>
                <strong>Trazabilidad completa:</strong> los archivos XML y PDF
                firmados quedan adjuntos al documento de QuickBooks.
              </li>
              <li>
                <strong>Cumplimiento DGII:</strong> la transmisión, firma
                digital y validación con la DGII se manejan de forma
                transparente.
              </li>
              <li>
                <strong>Renovación automática de tokens:</strong> la sesión con
                QuickBooks se mantiene viva sin intervención del usuario.
              </li>
            </ul>
          </div>

          {/* Section 3 */}
          <div className="legal-section">
            <h2>3. Alcance de la integración</h2>
            <p>
              La integración utiliza la <strong>API oficial de Intuit</strong>{" "}
              con autenticación <strong>OAuth 2.0</strong> y el alcance
              (scope) <strong>com.intuit.quickbooks.accounting</strong>.
            </p>
            <p><strong>Documentos soportados:</strong></p>
            <ul>
              <li>Invoice → Factura electrónica (e-CF tipo 31, 32, 44, 45, 46)</li>
              <li>Purchase Order → Comprobante de Compras (e-CF tipo 41)</li>
              <li>Credit Memo → Nota de Crédito Electrónica (e-CF tipo 34)</li>
            </ul>
            <p><strong>Datos de referencia consultados:</strong></p>
            <ul>
              <li>Customer (clientes), Vendor (suplidores)</li>
              <li>Item (productos y servicios)</li>
              <li>TaxRate y TaxCode (impuestos: ITBIS, ISR, propinas)</li>
              <li>CompanyInfo (datos del emisor)</li>
            </ul>
            <p>
              La integración opera contra los entornos <strong>Sandbox</strong>{" "}
              (pruebas) y <strong>Production</strong> (producción) de QuickBooks
              Online, y se ejecuta de forma asincrónica en segundo plano.
            </p>
          </div>

          {/* Section 4 — DATA FLOW */}
          <div className="legal-section">
            <h2>4. Data Flow de la Integración</h2>
            <p>
              El flujo completo de datos entre QuickBooks Online, Citrus eCF y
              la DGII es el siguiente:
            </p>

            {/* CSS-only diagram */}
            <div className="qb-flow-diagram">
              <div className="qb-flow-box qb-flow-box-qb">
                <div className="qb-flow-box-logo">
                  <img src={quickbooksLogo} alt="QuickBooks Online" />
                </div>
                <div className="qb-flow-box-title">QuickBooks Online</div>
                <div className="qb-flow-box-subtitle">Sistema contable</div>
              </div>

              <div className="qb-flow-arrow qb-flow-arrow-right">
                <span className="qb-flow-step-label">1) READ</span>
                <span className="qb-flow-arrow-line">→</span>
                <span className="qb-flow-step-sub">Invoices, POs, Credit Memos</span>
              </div>

              <div className="qb-flow-box qb-flow-box-citrus">
                <div className="qb-flow-box-logo">
                  {theme && theme.logo ? (
                    <img src={theme.logo} alt="Citrus eCF" />
                  ) : (
                    <div className="qb-flow-box-icon">eCF</div>
                  )}
                </div>
                <div className="qb-flow-box-title">Citrus eCF</div>
                <div className="qb-flow-box-subtitle">Backend de procesamiento</div>
              </div>

              <div className="qb-flow-arrow qb-flow-arrow-right">
                <span className="qb-flow-step-label">2) SUBMIT</span>
                <span className="qb-flow-arrow-line">→</span>
                <span className="qb-flow-step-sub">e-CF firmado (XML)</span>
              </div>

              <div className="qb-flow-box qb-flow-box-dgii">
                <div className="qb-flow-box-logo">
                  <img src={dgiiLogo} alt="DGII" />
                </div>
                <div className="qb-flow-box-title">DGII</div>
                <div className="qb-flow-box-subtitle">Autoridad fiscal RD</div>
              </div>
            </div>

            {/* Reverse arrow row */}
            <div className="qb-flow-diagram qb-flow-diagram-reverse">
              <div className="qb-flow-arrow qb-flow-arrow-left">
                <span className="qb-flow-step-label">4) WRITE-BACK</span>
                <span className="qb-flow-arrow-line">←</span>
                <span className="qb-flow-step-sub">NCF + PDF + XML</span>
              </div>
              <div className="qb-flow-arrow qb-flow-arrow-left">
                <span className="qb-flow-step-label">3) RESPONSE</span>
                <span className="qb-flow-arrow-line">←</span>
                <span className="qb-flow-step-sub">Aprobado / Rechazado</span>
              </div>
            </div>

            <h3>4.1 Datos leídos desde QuickBooks Online</h3>
            <div className="legal-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Entidad QuickBooks</th>
                    <th>Campos accedidos</th>
                    <th>Propósito</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><strong>Customer</strong></td>
                    <td>DisplayName, AlternatePhone (RNC/Cédula)</td>
                    <td>Identificar al receptor del e-CF</td>
                  </tr>
                  <tr>
                    <td><strong>Vendor</strong></td>
                    <td>DisplayName, AlternatePhone (RNC)</td>
                    <td>Identificar al suplidor en compras</td>
                  </tr>
                  <tr>
                    <td><strong>Invoice</strong></td>
                    <td>
                      TxnDate, DocNumber, CustomerRef, CurrencyRef,
                      ExchangeRate, DueDate, CustomField, Line[]
                    </td>
                    <td>Generar Factura Electrónica</td>
                  </tr>
                  <tr>
                    <td><strong>PurchaseOrder</strong></td>
                    <td>
                      VendorRef, líneas y items de retención (ISR / ITBIS)
                    </td>
                    <td>Generar Comprobante de Compras</td>
                  </tr>
                  <tr>
                    <td><strong>CreditMemo</strong></td>
                    <td>Campos similares a Invoice (filtrado por Balance = 0)</td>
                    <td>Generar Nota de Crédito Electrónica</td>
                  </tr>
                  <tr>
                    <td><strong>TaxRate / TaxCode</strong></td>
                    <td>RateValue, Name</td>
                    <td>Mapear ITBIS, ISR y otros impuestos dominicanos</td>
                  </tr>
                  <tr>
                    <td><strong>Item</strong></td>
                    <td>ItemRef, Type, Name, UnitPrice</td>
                    <td>Determinar bien (1) o servicio (2) en el e-CF</td>
                  </tr>
                  <tr>
                    <td><strong>CompanyInfo</strong></td>
                    <td>
                      LegalName, CompanyAddr, PrimaryPhone, Email
                    </td>
                    <td>Datos del emisor en el comprobante</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3>4.2 Datos escritos de vuelta a QuickBooks Online</h3>
            <div className="legal-table-wrapper">
              <table>
                <thead>
                  <tr>
                    <th>Destino en QuickBooks</th>
                    <th>Campo</th>
                    <th>Valor escrito</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>CustomField (Invoice / PO / CreditMemo)</td>
                    <td><strong>NCF</strong></td>
                    <td>Número de Comprobante Fiscal Electrónico asignado por la DGII</td>
                  </tr>
                  <tr>
                    <td>CustomField (Invoice / PO / CreditMemo)</td>
                    <td><strong>TransaccionId</strong></td>
                    <td>ID interno de la transacción en Citrus eCF para trazabilidad</td>
                  </tr>
                  <tr>
                    <td>CustomField (Invoice / PO / CreditMemo)</td>
                    <td><strong>Tipo Comprobante Electronico</strong></td>
                    <td>Tipo de e-CF DGII (31, 32, 33, 34, 41, 43, 44, 45, 46, 47)</td>
                  </tr>
                  <tr>
                    <td>Attachable (adjunto al documento)</td>
                    <td><strong>PDF</strong></td>
                    <td>Representación visual del e-CF firmado</td>
                  </tr>
                  <tr>
                    <td>Attachable (adjunto al documento)</td>
                    <td><strong>XML</strong></td>
                    <td>Archivo electrónico oficial firmado y enviado a la DGII</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="legal-notice">
              <strong>Importante:</strong> Citrus eCF nunca modifica los
              importes, líneas ni montos originales de sus documentos en
              QuickBooks. Solo añade campos personalizados y archivos
              adjuntos con la información fiscal resultante.
            </div>
          </div>

          {/* Section 5 — How to Connect */}
          <div className="legal-section">
            <h2>5. Cómo Conectar tu Cuenta de QuickBooks</h2>
            <p>
              Conectar QuickBooks Online a Citrus eCF requiere una sola
              autorización OAuth. Sigue estos pasos:
            </p>
            <ol>
              <li>
                Inicia sesión en Citrus eCF y dirígete al menú{" "}
                <strong>Configuración → Compañía</strong>.
              </li>
              <li>
                En la sección <strong>Sistemas Externos</strong>, ubica el
                bloque <strong>QuickBooks</strong>.
              </li>
              <li>
                Haz clic en el botón verde{" "}
                <strong>"Connect to QuickBooks"</strong>. Se abrirá una
                ventana de Intuit en una pestaña nueva.
              </li>
              <li>
                Inicia sesión en tu cuenta de QuickBooks Online (si no estás
                ya autenticado) y selecciona la <strong>compañía</strong>{" "}
                que deseas vincular.
              </li>
              <li>
                Revisa los permisos solicitados (alcance{" "}
                <em>com.intuit.quickbooks.accounting</em>) y haz clic en{" "}
                <strong>"Connect"</strong>.
              </li>
              <li>
                Intuit redirigirá automáticamente a Citrus eCF y la
                vinculación quedará completada. Verás los datos de la
                compañía vinculada en pantalla.
              </li>
            </ol>
            <div className="legal-notice">
              <strong>¿Qué permisos otorgas al conectar?</strong> Citrus eCF
              solo solicita el alcance <em>Accounting</em> de QuickBooks
              Online, suficiente para leer los documentos contables y
              escribir campos personalizados / adjuntos. No solicitamos
              acceso a información de pagos, nómina ni datos personales del
              propietario de la cuenta.
            </div>
          </div>

          {/* Section 6 — How to Disconnect */}
          <div className="legal-section">
            <h2>6. Cómo Desconectar tu Cuenta de QuickBooks</h2>
            <p>
              Puedes revocar la integración en cualquier momento por dos
              vías. Ambas eliminan los tokens almacenados y detienen el
              procesamiento de nuevos documentos.
            </p>

            <h3>Opción A — Desde Citrus eCF</h3>
            <ol>
              <li>
                Ingresa a Citrus eCF y abre el menú{" "}
                <strong>Configuración → Compañía → Sistemas Externos</strong>.
              </li>
              <li>
                En el bloque QuickBooks, haz clic en el enlace{" "}
                <strong>"Desvincular Cuenta de QuickBooks"</strong>.
              </li>
              <li>
                Confirma en el diálogo de confirmación. Citrus eCF llamará
                al endpoint de revocación de Intuit y eliminará los tokens
                de su base de datos.
              </li>
            </ol>

            <h3>Opción B — Desde QuickBooks Online</h3>
            <ol>
              <li>
                Inicia sesión en{" "}
                <a
                  href="https://app.qbo.intuit.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: accentColor }}
                >
                  QuickBooks Online
                </a>
                .
              </li>
              <li>
                Ve a <strong>Apps → My Apps</strong> (o{" "}
                <em>Aplicaciones → Mis aplicaciones</em>).
              </li>
              <li>
                Localiza <strong>Citrus eCF</strong> en la lista de
                aplicaciones conectadas.
              </li>
              <li>
                Haz clic en <strong>"Disconnect"</strong> y confirma. Intuit
                invalidará los tokens emitidos y la integración se detendrá
                de inmediato.
              </li>
            </ol>

            <div className="legal-warning">
              <strong>Nota:</strong> Al desconectar, los documentos ya
              procesados conservan sus campos personalizados (NCF, etc.) y
              archivos adjuntos en QuickBooks. Solo se interrumpe el
              procesamiento de nuevos documentos.
            </div>
          </div>

          {/* Section 7 — Security & Privacy */}
          <div className="legal-section">
            <h2>7. Seguridad y Privacidad</h2>
            <ul>
              <li>
                <strong>OAuth 2.0:</strong> Citrus eCF nunca recibe ni
                almacena tu contraseña de QuickBooks. La autorización se
                realiza directamente con Intuit.
              </li>
              <li>
                <strong>Tokens encriptados:</strong> el access token y el
                refresh token se almacenan cifrados en la base de datos
                privada de Citrus.
              </li>
              <li>
                <strong>Renovación automática:</strong> el access token se
                renueva mediante el refresh token antes de cada operación,
                de forma silenciosa.
              </li>
              <li>
                <strong>Alcance mínimo:</strong> solo solicitamos el scope{" "}
                <em>Accounting</em>; no accedemos a información personal,
                pagos ni nómina.
              </li>
              <li>
                <strong>Transmisión TLS 1.2:</strong> toda comunicación con
                Intuit y la DGII se realiza sobre TLS 1.2 o superior.
              </li>
              <li>
                <strong>Revocación inmediata:</strong> al desvincular, los
                tokens son revocados en Intuit y eliminados de Citrus eCF.
              </li>
            </ul>
            <p>
              Para más detalles consulte nuestra{" "}
              <a href="/privacidad" style={{ color: accentColor }}>
                Política de Privacidad
              </a>{" "}
              y nuestro{" "}
              <a href="/eula" style={{ color: accentColor }}>
                Acuerdo de Licencia (EULA)
              </a>
              .
            </p>
          </div>

          {/* Section 8 — FAQ */}
          <div className="legal-section">
            <h2>8. Preguntas Frecuentes</h2>

            <h3>¿La integración modifica mis facturas en QuickBooks?</h3>
            <p>
              No. Citrus eCF nunca cambia importes, líneas, fechas, cliente
              ni totales. Solo añade <em>custom fields</em> con datos
              fiscales (NCF, ID de transacción, tipo de comprobante) y
              archivos adjuntos (PDF y XML firmados).
            </p>

            <h3>¿Cuánto tarda en procesarse un documento?</h3>
            <p>
              El procesador de Citrus eCF revisa nuevos documentos en
              QuickBooks cada pocos segundos. Una vez detectado, el
              documento se firma, se transmite a la DGII y el NCF se escribe
              de vuelta normalmente en menos de un minuto, dependiendo de
              la disponibilidad del servicio de la DGII.
            </p>

            <h3>¿Funciona con varias compañías de QuickBooks?</h3>
            <p>
              Sí. Cada compañía de Citrus eCF se vincula a una compañía de
              QuickBooks (un único <em>realmId</em>). Si gestionas varias
              empresas, cada una requiere su propia autorización OAuth.
            </p>

            <h3>¿Qué pasa si la DGII rechaza un documento?</h3>
            <p>
              El estado de rechazo y el motivo se registran en Citrus eCF y
              quedan visibles en su panel de control. El documento original
              en QuickBooks no se modifica hasta que el e-CF sea aprobado
              por la DGII.
            </p>

            <h3>¿Mis datos salen de la República Dominicana?</h3>
            <p>
              Los datos transitan únicamente entre QuickBooks Online (Intuit),
              los servidores de Citrus eCF y la DGII. No compartimos
              información con terceros fuera de este flujo.
            </p>
          </div>

          {/* Section 9 — Contact */}
          <div className="legal-section">
            <h2>9. Soporte y Contacto</h2>
            <p>
              ¿Tienes preguntas sobre la integración o necesitas ayuda con
              la vinculación? Contáctanos:
            </p>
            <p>
              <strong>Citrus Technology</strong>
              <br />
              Correo electrónico:{" "}
              <a
                href="mailto:soporte@citrus.com.do"
                style={{ color: accentColor }}
              >
                soporte@citrus.com.do
              </a>
              <br />
              Sitio web:{" "}
              <a
                href="https://citrus.com.do"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: accentColor }}
              >
                citrus.com.do
              </a>
            </p>
          </div>

          {/* Closing CTA banner — strongest conversion point, after user has read everything */}
          <div className="qb-cta-banner">
            <div className="qb-cta-banner-logos">
              {theme && theme.logo && (
                <div className="qb-cta-banner-icon">
                  <img src={theme.logo} alt="Citrus eCF" />
                </div>
              )}
              <div className="qb-cta-banner-connector" aria-hidden="true">+</div>
              <div className="qb-cta-banner-icon">
                <img src={quickbooksLogo} alt="QuickBooks" />
              </div>
            </div>
            <div className="qb-cta-banner-content">
              <h3>¿Listo para automatizar tu facturación electrónica?</h3>
              <p>
                Crea tu cuenta de Citrus eCF gratis, conecta QuickBooks Online
                en menos de un minuto y empieza a emitir e-CFs ante la DGII
                directamente desde tu sistema contable.
              </p>
              <ul className="qb-cta-banner-perks">
                <li>Sin tarjeta de crédito</li>
                <li>Configuración guiada</li>
                <li>Soporte en español</li>
              </ul>
            </div>
            <div className="qb-cta-banner-actions">
              <a
                href="https://citrus.com.do/pruebaGratis?producto=eCF"
                target="_blank"
                rel="noopener noreferrer"
                className="qb-cta-banner-button"
              >
                Comenzar Prueba Gratis
                <span className="qb-cta-banner-button-arrow">→</span>
              </a>
              <span className="qb-cta-banner-subtle">
                Te llevará a citrus.com.do
              </span>
            </div>
          </div>

          {/* Footer */}
          <div className="legal-footer">
            <a href="/" className="legal-back-link">
              &#8592; Volver al inicio
            </a>
            <div className="legal-nav-links">
              <a href="/eula">Acuerdo de Licencia (EULA)</a>
              <a href="/privacidad">Política de Privacidad</a>
            </div>
            <p className="legal-copyright">
              &copy; {new Date().getFullYear()} Citrus Technology. Todos los
              derechos reservados.
            </p>
          </div>

        </div>
      </div>

      {/* Floating sticky CTA — always accessible while scrolling */}
      <a
        href="https://citrus.com.do/pruebaGratis?producto=eCF"
        target="_blank"
        rel="noopener noreferrer"
        className="qb-floating-cta"
        aria-label="Crear cuenta — Prueba gratis de Citrus eCF"
      >
        <span className="qb-floating-cta-icon" aria-hidden="true">+</span>
        <span className="qb-floating-cta-text">Prueba Gratis</span>
      </a>
    </div>
  );
}
